<template>
  <div class="adaptive-result-page">
    <page-header :breadcrumbs="breadcrumbs">
      <template #topbar>
        {{ simulation?.name }}
      </template>
    </page-header>
    <data-loader :do-not-forward="true">
      <div
        v-if="result"
        class="adaptive-result"
        data-test="adaptive-result"
      >
        <div
          class="outcome"
          :class="passed ? 'outcome-pass' : 'outcome-fail'"
          data-test="adaptive-result-outcome"
        >
          <h2>{{ passed ? 'You passed!' : 'Not a pass this time' }}</h2>
          <p>{{ stopReasonText }}</p>
          <p class="details">
            You answered {{ result.itemsAnswered }} questions in {{ timeUsed }} on {{ formatDate(result.finishedAt) }}.
          </p>
        </div>

        <div class="list-container">
          <div class="list-row-container list-heading">
            <span>Client Needs</span>
            <span class="flex-1"></span>
            <span>Performance</span>
          </div>
          <div
            v-for="need in result.clientNeeds"
            :key="need.key"
            class="list-row-container"
            data-test="adaptive-result-need"
          >
            <count-pill
              icon-name="check"
              variant="00b46d"
              start
            >
              <span>{{ need.correct }}</span>
            </count-pill>
            <count-pill
              icon-name="cross"
              variant="dc3416"
              end
            >
              <span>{{ need.answered - need.correct }}</span>
            </count-pill>
            <span class="need-name">{{ need.name }}</span>
            <span class="flex-1"></span>
            <span
              class="rating"
              :class="`rating-${need.rating}`"
            >{{ ratingText[need.rating] }}</span>
          </div>
        </div>

        <div
          v-if="reviewItems.length"
          class="list-container"
          data-test="adaptive-review-list"
        >
          <div class="list-row-container list-heading">
            <span>Review Your Questions</span>
            <span class="flex-1"></span>
            <span>{{ reviewItems.filter(item => item.correct).length }} of {{ reviewItems.length }} correct</span>
          </div>
          <router-link
            v-for="item in reviewItems"
            :key="item.position"
            class="list-row-container"
            :to="{ name: 'adaptiveSim#review', params: { simId, sessionId, position: item.position } }"
          >
            <span
              class="review-mark"
              :class="item.correct ? 'review-right' : 'review-wrong'"
            >{{ item.correct ? '✓' : '✗' }}</span>
            <span>Question {{ item.position }}</span>
            <span class="flex-1"></span>
            <span class="review-type">{{ flashcardType(item) }}</span>
          </router-link>
        </div>

        <div
          v-if="pastAttempts.length"
          class="list-container"
        >
          <div class="list-row-container list-heading">
            <span>Past Attempts</span>
          </div>
          <router-link
            v-for="attempt in pastAttempts"
            :key="attempt.id"
            class="list-row-container"
            :to="{ name: 'adaptiveSim#result', params: { simId, sessionId: attempt.id } }"
          >
            <span>{{ formatDate(attempt.finishedAt) }}</span>
            <span class="flex-1"></span>
            <span>{{ attempt.itemsAnswered }} questions · {{ attempt.result === 'pass' ? 'Passed' : 'Did not pass' }}</span>
          </router-link>
        </div>

        <adaptive-debug-panel
          v-if="debug"
          :debug="debug"
        />

        <div class="actions">
          <button
            class="retake-button"
            data-test="adaptive-retake"
            @click="$router.push({ name: 'sims#show', params: { simId } })"
          >
            Take It Again
          </button>
        </div>
      </div>
    </data-loader>
  </div>
</template>

<script>
import PageHeader from '@components/singletons/pageHeader';
import DataLoader from '@/components/sims/DataLoader.vue';
import CountPill from '@/components/sims/CountPill.vue';
import AdaptiveDebugPanel from '@/components/sims/adaptive/AdaptiveDebugPanel.vue';
import WuxSimulationsMixin from '@/mixins/wux-simulations';

// What ended the test, worded for a pass or a fail
const stopReasons = {
  confidence: {
    pass: 'You were consistently performing above the passing standard, so the test ended as soon as it was sure. That\'s exactly how the real NCLEX works. Great job!',
    fail: 'Your answers stayed below the passing standard long enough for the test to be sure, so it ended early, just like the real NCLEX. Use the breakdown below to see where to focus next.',
  },
  max_length: {
    pass: 'You went the full 150 questions. It was close, but you finished above the passing standard. Well done for staying with it.',
    fail: 'You went the full 150 questions. It was close, but you finished just below the passing standard. You\'re nearer than it may feel.',
  },
  timeout: {
    pass: 'Time ran out, but you\'d answered enough questions and were above the passing standard when it did.',
    fail: 'Time ran out before the test could finish. Pacing matters on the real NCLEX too, so practice keeping a steady rhythm.',
  },
  pool_exhausted: {
    pass: 'You worked through every question we had at your level, and you were above the passing standard.',
    fail: 'You worked through every question we had at your level, and you finished below the passing standard.',
  },
};

export default {
  components: {
    AdaptiveDebugPanel,
    CountPill,
    DataLoader,
    PageHeader,
  },
  mixins: [WuxSimulationsMixin],
  data() {
    return {
      ratingText: {
        above: 'Above passing standard',
        near: 'Near passing standard',
        below: 'Below passing standard',
      },
    };
  },
  computed: {
    breadcrumbs() {
      return [{ name: this.simulation?.name }];
    },
    sessionId() {
      return +this.$route.params.sessionId;
    },
    result() {
      const session = this.$store.getters['adaptiveSim/session'];
      return session?.id === this.sessionId ? this.$store.getters['adaptiveSim/result'] : null;
    },
    debug() {
      return this.result ? this.$store.getters['adaptiveSim/debug'] : null;
    },
    passed() {
      return this.result?.result === 'pass';
    },
    stopReasonText() {
      return stopReasons[this.result?.stopReason]?.[this.passed ? 'pass' : 'fail'] || '';
    },
    timeUsed() {
      if (!this.result) return '';
      const minutes = Math.round((Date.parse(this.result.finishedAt) - Date.parse(this.result.startedAt)) / 60000);
      const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
      const hours = Math.floor(minutes / 60);
      if (!hours) return plural(minutes, 'minute');
      return minutes % 60 ? `${plural(hours, 'hour')} ${plural(minutes % 60, 'minute')}` : plural(hours, 'hour');
    },
    reviewItems() {
      const review = this.$store.getters['adaptiveSim/review'];
      return review?.sessionId === this.sessionId ? review.items : [];
    },
    pastAttempts() {
      const history = this.$store.getters['adaptiveSim/history'](this.simId) || [];
      return history.filter(attempt => attempt.status === 'completed' && attempt.id !== this.sessionId);
    },
  },
  watch: {
    sessionId: {
      immediate: true,
      handler() {
        this.load();
      },
    },
  },
  methods: {
    load() {
      return this.$store.dispatch('adaptiveSim/fetchSession', this.sessionId).then(({ catSession }) => {
        if (catSession.status === 'in_progress') {
          return this.$router.replace({ name: 'adaptiveSim#show', params: this.$route.params });
        }
        return this.$store.dispatch('adaptiveSim/fetchReview', this.sessionId);
      }).catch(error => console.error(error));
    },
    flashcardType(item) {
      return this.$store.getters['flashcards/flashcards'][item.flashcardId]?.type || '';
    },
    formatDate(timestamp) {
      if (!timestamp) return '';
      return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(new Date(timestamp));
    },
  },
};
</script>

<style scoped lang="scss">
.adaptive-result-page {
  background: #EDF1F5;
  min-height: 100%;
}

.adaptive-result {
  max-width: 60rem;
  margin: 2rem auto;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.outcome {
  border-radius: .5rem;
  padding: 1.5rem 2rem;
  background: white;
  border-left: .5rem solid;

  h2 {
    margin: 0 0 .5rem;
  }

  .details {
    color: #516173;
    margin-bottom: 0;
  }
}

.outcome-pass {
  border-color: #00b46d;
}

.outcome-fail {
  border-color: #dc3416;
}

.list-heading {
  font-weight: 600;
}

.need-name {
  color: black;
  margin-left: 1rem;
}

.review-mark {
  font-weight: 600;
  margin-right: 1rem;
}

.review-right {
  color: #00b46d;
}

.review-wrong {
  color: #dc3416;
}

.review-type {
  color: #516173;
}

.rating-above {
  color: #00b46d;
}

.rating-near {
  color: #516173;
}

.rating-below {
  color: #dc3416;
}

.actions {
  display: flex;
  justify-content: center;
}

.retake-button {
  border-radius: 4px;
  color: white;
  font-weight: 600;
  padding: .5rem 1.5rem;
  background-color: #7bd56e;
  border: solid 1px #58AE4D;
}
</style>
